'use client'

import { useEffect } from 'react'

import Image from 'next/image'
import { useParams } from 'next/navigation'

import { SplitsLink } from '~/components/Link'
import { SignInButton } from '~/components/auth/SignInButton'
import { EmptyState } from '~/components/component-library'
import { TransactionDisplay } from '~/components/txn-display'
import {
  TransactionPageLayout,
  TransactionPageLoadingSkeleton,
} from '~/components/txn-display/TransactionPageLayout'
import { useBatchOperationRemover } from '~/hooks/useBatchOperationRemover'
import { useNavigation } from '~/hooks/useNavigation'
import { useSmartAccount } from '~/hooks/useSmartAccounts'
import { useTransaction } from '~/hooks/useTransactions'
import { OrgTransaction, TransactionStatus } from '~/types'
import { EvmAddress } from '~/utils/address'
import { isDraftBatchTransaction } from '~/utils/smartAccounts/batch'

export function TransactionPage() {
  const params = useParams<{ account: string; transaction: string }>()
  const navigate = useNavigation()
  const address = EvmAddress.format(params.account)
  const transactionId = params.transaction

  const { smartAccount, isLoading: isLoadingSmartAccount } =
    useSmartAccount(address)

  const { transaction, isLoading: isLoadingTransaction } =
    useTransaction(transactionId)

  const isLoading = isLoadingSmartAccount || isLoadingTransaction

  // Drafted single transfers/swaps need the edit dialog on the account page.
  // Drafted batches render fine here, so keep them on the dedicated page.
  const shouldRedirectDraft =
    transaction?.status === TransactionStatus.Drafted &&
    !isDraftBatchTransaction(transaction)

  useEffect(() => {
    if (shouldRedirectDraft) {
      navigate(`/accounts/${address}?transactionId=${transactionId}`)
    }
  }, [navigate, address, transactionId, shouldRedirectDraft])

  if (isLoading || shouldRedirectDraft) {
    return <TransactionPageLoadingSkeleton />
  }

  if (!smartAccount || !transaction) {
    return <TransactionNotFoundState smartAccountFound={!!smartAccount} />
  }

  return (
    <TransactionPageLayout
      footer={
        transaction.isPublicTransaction ? <SplitsTeamsFooter /> : undefined
      }
    >
      {transaction.isPublicTransaction ? (
        <TransactionDisplay transaction={transaction} includeTitleAndIcon />
      ) : (
        <RemovableBatchTransactionDisplay
          transaction={transaction}
          smartAccountAddress={address}
          isPublicSmartAccount={!!smartAccount.isPublicSmartAccount}
        />
      )}
    </TransactionPageLayout>
  )
}

const RemovableBatchTransactionDisplay = ({
  transaction,
  smartAccountAddress,
  isPublicSmartAccount,
}: {
  transaction: OrgTransaction
  smartAccountAddress: string
  isPublicSmartAccount: boolean
}) => {
  const navigate = useNavigation()
  const canRemove =
    !isPublicSmartAccount && isDraftBatchTransaction(transaction)

  const { handleRemoveOperation, removingOperationId } =
    useBatchOperationRemover({
      transaction,
      onBatchEmptied: () => navigate(`/accounts/${smartAccountAddress}`),
    })

  return (
    <TransactionDisplay
      transaction={transaction}
      includeTitleAndIcon
      allowRemoveBatchOperation={canRemove}
      removingBatchOperationId={removingOperationId}
      onRemoveBatchOperation={canRemove ? handleRemoveOperation : undefined}
    />
  )
}

export const TransactionNotFoundState = ({
  smartAccountFound,
}: {
  smartAccountFound: boolean
}) => {
  return (
    <div className="flex min-h-dvh w-full flex-col items-center justify-center bg-surface px-4">
      <div className="relative w-full px-4 py-4 md:max-w-md md:rounded-2xl md:border md:border-muted md:p-8">
        <EmptyState
          fullScreen
          title={
            !smartAccountFound
              ? 'Smart account not found'
              : 'Transaction not found'
          }
        >
          <div className="space-y-4">
            <p>
              {!smartAccountFound
                ? 'Please check the account address and try again.'
                : 'Please check the transaction ID and try again.'}
            </p>
            <div className="space-y-4 text-center">
              <p>You may need to sign in to view this transaction.</p>
              <div className="flex justify-center">
                <SignInButton text="Sign in" />
              </div>
            </div>
          </div>
        </EmptyState>
      </div>
    </div>
  )
}

export const SplitsTeamsFooter = () => {
  return (
    <div>
      <SplitsLink
        path="https://app.splits.org"
        target="tab"
        className="flex flex-row items-center justify-center space-x-2"
      >
        {/* opacity tints the logo to roughly match text-secondary in both light and dark modes */}
        <div className="opacity-65">
          <Image
            src={'/logo_dark.svg'}
            alt={'splits logo'}
            width={24}
            height={48}
            className="hidden dark:block"
          />
          <Image
            src={'/logo_light.svg'}
            alt={'splits logo'}
            width={24}
            height={48}
            className="block dark:hidden"
          />
        </div>
        <div className="font-medium text-secondary">Powered by Splits</div>
      </SplitsLink>
    </div>
  )
}
